import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FiLock, FiMail, FiUser, FiArrowLeft, FiEye, FiEyeOff } from 'react-icons/fi';
import { useAuth } from '../utils/auth';
import { ensureValidTenantId } from '../utils/auth';
import Modal from '../components/Modals/Modal';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState(1); // 1: Account details
  const [formData, setFormData] = useState({
    accountName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  // Remove all OTP-related state
  const [accountId, setAccountId] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // Remove all OTP-related state
  const [demoOtp, setDemoOtp] = useState('');

  // Removed tenantId redirect useEffect

  const generateAccountId = () => {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 8);
    return `HB${timestamp}${random}`.toUpperCase();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const strongPasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.accountName.trim()) {
      newErrors.accountName = 'Account name is required';
    } else if (formData.accountName.length < 3) {
      newErrors.accountName = 'Account name must be at least 3 characters';
    }
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (!strongPasswordRegex.test(formData.password)) {
      newErrors.password = 'Password must be at least 8 characters, include uppercase, lowercase, number, and special character.';
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    if (!formData.phone) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setIsLoading(true);
    const generatedAccountId = generateAccountId();
    setAccountId(generatedAccountId);
    try {
      // Register user and account
      const registerRes = await fetch(`${API_BASE_URL}/api/auth/register-with-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          accountName: formData.accountName,
          accountId: generatedAccountId,
          phone: formData.phone
        })
      });
      if (!registerRes.ok) {
        const errorData = await registerRes.json();
        const message = (errorData && errorData.message) || '';
        if (typeof message === 'string' && message.toLowerCase().includes('email already exists')) {
          setErrors({ email: 'This email is already registered. Please sign in or use another email.' });
        } else if (typeof message === 'string') {
          setErrors({ accountName: message });
        } else {
          setErrors({ accountName: 'Account creation failed. Please try again.' });
        }
        return;
      }
      // Auto-login after registration
      const loginRes = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: formData.email,
          password: formData.password 
        })
      });
      if (!loginRes.ok) throw new Error('Failed to login after registration');
      const loginData = await loginRes.json();
      localStorage.setItem('tenantId', loginData.accountId || generatedAccountId);
      const userData = {
        email: formData.email,
        name: formData.accountName,
        accountId: generatedAccountId
      };
      ensureValidTenantId();
      login(loginData.access_token, userData);
      navigate('/onboarding');
    } catch (err) {
      setErrors({ accountName: 'Registration failed. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };
  // Add this function to fix the linter error
  const handleBackToTenant = () => {
    localStorage.removeItem('tenantId');
    navigate('/');
  };
  // Only render the registration form, no OTP UI
  return (
    <div className="auth-container">
      <div className="auth-card">
        <button 
          onClick={handleBackToTenant}
          className="back-button"
          type="button"
        >
          <FiArrowLeft /> Back to Account Selection
        </button>
        <div className="auth-header">
          <div className="brand-logo">
            <h2>Hamara Bill</h2>
            <p className="brand-subtitle">By TFS Traders</p>
          </div>
          <h1>Create New Account</h1>
          <p className="text-muted">Set up your bill management account</p>
        </div>
        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label className="form-label">Account Name</label>
            <div className="input-group">
              <FiUser className="input-icon" />
              <input
                type="text"
                name="accountName"
                className="form-input"
                placeholder="Enter your business name"
                value={formData.accountName}
                onChange={handleChange}
                disabled={isLoading}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Email Address</label>
            <div className="input-group">
              <FiMail className="input-icon" />
              <input
                type="email"
                name="email"
                className="form-input"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
                disabled={isLoading}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Phone Number</label>
            <div className="input-group">
              <FiUser className="input-icon" />
              <input
                type="tel"
                name="phone"
                className="form-input"
                placeholder="Enter your phone number"
                value={formData.phone}
                onChange={handleChange}
                disabled={isLoading}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Password</label>
            <div className="input-group">
              <FiLock className="input-icon" />
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                className="form-input"
                placeholder="Enter your password"
                value={formData.password}
                onChange={handleChange}
                disabled={isLoading}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Confirm Password</label>
            <div className="input-group">
              <FiLock className="input-icon" />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                className="form-input"
                placeholder="Confirm your password"
                value={formData.confirmPassword}
                onChange={handleChange}
                disabled={isLoading}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                tabIndex={-1}
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
          </div>
          {/* Show all errors in a single error box at the bottom */}
          {Object.keys(errors).length > 0 && (
            <div className="form-error-box" style={{ background: '#ffeaea', color: '#c0392b', borderRadius: 8, padding: 16, marginBottom: 16, fontSize: '1rem' }}>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {Object.values(errors).map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            </div>
          )}
          <button 
            type="submit" 
            className={`btn btn-primary btn-block ${isLoading ? 'loading' : ''}`}
            disabled={isLoading}
            style={{ marginTop: 8 }}
          >
            {isLoading ? 'Creating Account...' : 'Create Account'}
          </button>
          <p className="text-center text-muted mt-4">
            Already have an account? <Link to="/login" className="link-primary">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default RegisterPage; 